import { sectionRenderer } from '@theme/section-renderer';
import { startViewTransition } from '@theme/utilities';

class ProductHeaderTabs extends HTMLElement {
  /** @type {Set<string>} */
  #prefetched = new Set();

  connectedCallback() {
    this.sectionRoot = this.closest('.shopify-section') || document;
    this._onTabClick = this.handleTabClick.bind(this);
    this._onTabHover = this.prefetchCollection.bind(this);

    this.getTabButtons().forEach((button) => {
      button.addEventListener('click', this._onTabClick);
      button.addEventListener('pointerenter', this._onTabHover);
    });

    this.updateBannerContent(this.sectionRoot.querySelector('.product-header-tabs__btn.active'));

    this._onPopState = this.handlePopState.bind(this);
    window.addEventListener('popstate', this._onPopState);
  }

  disconnectedCallback() {
    this.getTabButtons().forEach((button) => {
      button.removeEventListener('click', this._onTabClick);
      button.removeEventListener('pointerenter', this._onTabHover);
    });
    window.removeEventListener('popstate', this._onPopState);
  }

  getTabButtons() {
    return this.sectionRoot.querySelectorAll('.product-header-tabs__btn');
  }

  /** @returns {string | null} The section id of the main product grid, if present. */
  getMainSectionId() {
    const element = document.querySelector('.product-grid-container');
    return element ? element.getAttribute('section-id') || 'main' : null;
  }

  /**
   * Resolve a tab's target URL relative to the current page, or null if it's a
   * no-op (missing, '#', or the page we're already on).
   * @param {HTMLElement} button
   * @returns {URL | null}
   */
  resolveTarget(button) {
    const href = button.getAttribute('href');
    if (!href || href === '#') return null;

    const target = new URL(href, window.location.origin);
    const current = new URL(window.location.href);
    if (current.pathname === target.pathname && current.search === target.search) return null;

    return target;
  }

  /**
   * Warm the section cache on hover so the subsequent click renders from cache.
   * Uses getSectionHTML (fetch + cache only) so the DOM is never touched here.
   * @param {PointerEvent} event
   */
  prefetchCollection(event) {
    const button = /** @type {HTMLElement} */ (event.currentTarget);
    const href = button.getAttribute('href');
    if (!href || this.#prefetched.has(href)) return;

    const target = this.resolveTarget(button);
    if (!target) return;

    const mainSection = this.getMainSectionId();
    if (!mainSection) return;

    this.#prefetched.add(href);

    sectionRenderer.getSectionHTML(mainSection, true, target).catch(() => this.#prefetched.delete(href));

    const headerSectionId = this.getAttribute('data-section-id');
    if (headerSectionId) sectionRenderer.getSectionHTML(headerSectionId, true, target).catch(() => {});
  }

  /**
   * @param {MouseEvent} event
   */
  async handleTabClick(event) {
    event.preventDefault();
    const button = /** @type {HTMLElement} */ (event.currentTarget);
    const target = this.resolveTarget(button);
    if (!target) return;

    // Update active visual state immediately
    this.getTabButtons().forEach((btn) => {
      btn.classList.remove('active');
      btn.setAttribute('aria-selected', 'false');
    });
    button.classList.add('active');
    button.setAttribute('aria-selected', 'true');
    this.updateBannerContent(button);

    // Update browser URL, then re-render the collection in place
    history.pushState(null, '', button.getAttribute('href'));
    await this.loadCollection(target.href);
  }

  handlePopState() {
    this.loadCollection(window.location.href);
  }

  /**
   * @param {HTMLElement | null} button
   */
  updateBannerContent(button) {
    if (!button) return;

    const titleEl = this.querySelector('.product-header-tabs__title');
    const title = button.dataset.bannerTitle || '';
    const fruitMap = [
      ['grapes', this.querySelector('.fruit--grapes')],
      ['strawberry', this.querySelector('.fruit--strawberry')],
      ['raspberry', this.querySelector('.fruit--raspberry')],
      ['blueberry', this.querySelector('.fruit--blueberry')],
    ];

    if (titleEl && title) {
      titleEl.innerHTML = title
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\r\n|\r|\n/g, '<br>');
    }

    fruitMap.forEach(([fruitName, imageEl]) => {
      if (!imageEl) return;
      const key = `fruit${fruitName.charAt(0).toUpperCase()}${fruitName.slice(1)}`;
      const src = button.dataset[key];
      const alt = button.dataset[`${key}Alt`] || title || '';

      if (src) {
        imageEl.hidden = false;
        imageEl.src = src;
        imageEl.alt = alt;
      } else {
        imageEl.hidden = true;
        imageEl.removeAttribute('src');
        imageEl.alt = '';
      }
    });
  }

  /**
   * @param {string} url
   */
  async loadCollection(url) {
    const targetUrl = new URL(url, window.location.origin);
    const mainSectionElement = document.querySelector('.product-grid-container');

    if (!mainSectionElement) {
      window.location.href = url;
      return;
    }

    const mainSection = mainSectionElement.getAttribute('section-id') || 'main';
    const headerSectionId = this.getAttribute('data-section-id');
    const headingSectionElement = document.getElementById('shopify-section-section');

    try {
      await startViewTransition(async () => {
        const promises = [
          sectionRenderer.renderSection(mainSection, { url: targetUrl }),
          sectionRenderer.renderSection(headerSectionId, { url: targetUrl }),
        ];

        if (headingSectionElement) {
          promises.push(sectionRenderer.renderSection('section', { url: targetUrl }));
        }

        const [mainHtml, headerHtml, headingHtml] = await Promise.all(promises);

        if (
          mainHtml?.includes('Liquid error') ||
          headerHtml?.includes('Liquid error') ||
          (headingHtml && headingHtml.includes('Liquid error'))
        ) {
          throw new Error('Liquid rendering error detected in AJAX response.');
        }
      }, ['product-grid']);
    } catch (error) {
      console.warn('AJAX transition failed, falling back to full redirect:', error);
      window.location.href = url;
    }
  }
}

if (!customElements.get('product-header-tabs')) {
  customElements.define('product-header-tabs', ProductHeaderTabs);
}
